import type {
  ColumnSizingState,
  SortingState,
  RowSelectionState,
} from "@tanstack/react-table";

import {
  URL_SORT_PARAMS,
  URL_PAGE_PARAM,
  SORT_DIRECTIONS,
  STORAGE_KEY_PREFIX,
  STORAGE_KEY_SUFFIX,
} from "./constants";

export const getSearchParams = () =>
  new URLSearchParams(window.location.search);

export const readSortFromURL = (): SortingState => {
  const params = getSearchParams();
  const sortBy = params.get(URL_SORT_PARAMS.SORT_BY);
  const orderBy = params.get(URL_SORT_PARAMS.ORDER_BY);

  if (!sortBy) return [];

  return [{ id: sortBy, desc: orderBy === SORT_DIRECTIONS.DESC }];
};

export const writeSortToURL = (sorting: SortingState) => {
  const url = new URL(window.location.href);

  if (sorting.length === 0) {
    url.searchParams.delete(URL_SORT_PARAMS.SORT_BY);
    url.searchParams.delete(URL_SORT_PARAMS.ORDER_BY);
  } else {
    const { id, desc } = sorting[0];
    url.searchParams.set(URL_SORT_PARAMS.SORT_BY, id);
    url.searchParams.set(
      URL_SORT_PARAMS.ORDER_BY,
      desc ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC
    );
  }

  window.history.pushState({}, "", url.toString());
};

export const readPageFromURL = (): number => {
  const params = new URLSearchParams(window.location.search);
  const page = params.get(URL_PAGE_PARAM);

  return page ? Math.max(1, parseInt(page, 10)) : 1;
};

export const writePageToURL = (page: number) => {
  const url = new URL(window.location.href);

  if (page <= 1) {
    url.searchParams.delete(URL_PAGE_PARAM);
  } else {
    url.searchParams.set(URL_PAGE_PARAM, String(page));
  }

  window.history.pushState({}, "", url.toString());
};

export const keysToRowSelection = (keys: string[]): RowSelectionState =>
  Object.fromEntries(keys.map(key => [key, true]));

export const rowSelectionToKeys = (selection: RowSelectionState): string[] =>
  Object.keys(selection).filter(key => selection[key]);

export const hashPathname = (): string => {
  const pathname = decodeURIComponent(window.location.pathname).replace(
    /^\//,
    ""
  );
  let hash = 0;

  for (let i = 0; i < pathname.length; i++) {
    const char = pathname.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(16).toUpperCase();
};

export const getStorageKey = (
  prefix?: string,
  suffix: string = STORAGE_KEY_SUFFIX
): string => {
  const resolvedPrefix = prefix || hashPathname();

  return `${STORAGE_KEY_PREFIX}-${resolvedPrefix}-${suffix}`;
};

export const readFromLocalStorage = (key: string): string[] => {
  try {
    const stored = localStorage.getItem(key);

    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const writeToLocalStorage = (key: string, value: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if storage is full
  }
};

export const readSizingFromLocalStorage = (key: string): ColumnSizingState => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") return {};

    const result: ColumnSizingState = {};
    for (const [id, size] of Object.entries(parsed)) {
      if (typeof size === "number" && Number.isFinite(size)) {
        result[id] = size;
      }
    }

    return result;
  } catch {
    return {};
  }
};

export const writeSizingToLocalStorage = (
  key: string,
  value: ColumnSizingState
) => {
  try {
    if (Object.keys(value).length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Silently fail if storage is full
  }
};

export const getResizeColumnStyle = ({
  size,
  isSelectionCol,
  isLastDataCol,
  enableResize,
  pinned,
  pinnedStart,
  pinnedEnd,
  percentageWidth,
}: {
  size: number;
  isSelectionCol: boolean;
  isLastDataCol: boolean;
  enableResize: boolean;
  pinned: false | "left" | "right";
  pinnedStart?: number;
  pinnedEnd?: number;
  percentageWidth?: string;
}): React.CSSProperties => ({
  width: isLastDataCol
    ? undefined
    : percentageWidth && !isSelectionCol
      ? percentageWidth
      : size,
  ...(enableResize
    ? {
        minWidth: size,
        ...(!isSelectionCol && !isLastDataCol && !percentageWidth
          ? { maxWidth: size }
          : {}),
      }
    : {}),
  ...(pinned === "left" ? { left: pinnedStart } : {}),
  ...(pinned === "right" ? { right: pinnedEnd } : {}),
});

export const getPageNumbers = (
  current: number,
  total: number
): (number | "ellipsis")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [1];

  if (current > 3) pages.push("ellipsis");

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) pages.push("ellipsis");

  pages.push(total);

  return pages;
};
